import { useState, type FormEvent } from 'react';
import { useRouter } from '@/domains/shared/router/Router';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Input } from '@/domains/shared/components/Input';
import { Button } from '@/domains/shared/components/Button';

const isProduction =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_APP_ENV === 'production';

export function AdminLoginPage() {
  const { navigate } = useRouter();
  const { login } = useAdminAuth();

  const [email, setEmail] = useState(() =>
    isProduction ? '' : 'israel@decolacont.com.br',
  );
  const [password, setPassword] = useState(() =>
    isProduction ? '' : '••••••••••••',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);
    const res = await login(email, password);
    setIsLoading(false);

    if (res.success) {
      navigate('/admin/dashboard');
    } else {
      setErrorMessage(res.error || 'Falha ao realizar login.');
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-navy px-4 text-center text-brand-offWhite">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-navy-dark p-8 shadow-2xl">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-orange text-lg font-black text-white shadow-md">
          IC
        </div>

        {isProduction ? (
          <span className="inline-block rounded-full bg-feedback-success/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-feedback-success">
            Produção Segura • Supabase Auth
          </span>
        ) : (
          <span className="inline-block rounded-full bg-brand-orange/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-orange">
            Ambiente Local de Desenvolvimento (Mock de Interface)
          </span>
        )}

        <h1 className="mt-3 text-2xl font-black text-white">Painel Administrativo</h1>
        <p className="mt-2 text-xs text-brand-offWhite/70">
          Acesso restrito para gestão e qualificação dos leads contábeis.
        </p>

        {/* Mensagem de Erro */}
        {errorMessage && (
          <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 p-3.5 text-left text-xs text-red-300">
            <span className="block font-bold mb-0.5">Erro de Autenticação:</span>
            {errorMessage}
          </div>
        )}

        {/* Formulário de Login */}
        <form onSubmit={handleSubmit} className="mt-5 space-y-4 text-left">
          <Input
            label="E-mail Administrativo"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seu.email@exemplo.com"
            required
          />

          <Input
            label={isProduction ? 'Senha de Acesso' : 'Senha (Simulada)'}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••••"
            required
          />

          {!isProduction && (
            <div className="rounded-xl bg-white/5 border border-white/10 p-3.5 text-[11px] text-brand-offWhite/70 space-y-1.5">
              <span className="font-semibold text-brand-orange block">
                Aviso de Desenvolvimento:
              </span>
              <p className="leading-relaxed">
                Esta tela simula o comportamento de autenticação exclusivamente para testes locais.
                Nenhuma senha real é manipulada ou armazenada.
              </p>
              <div className="pt-1 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEmail('israel@decolacont.com.br');
                    setPassword('••••••••••••');
                    setErrorMessage(null);
                  }}
                  className="text-[10px] underline hover:text-white text-brand-orange"
                >
                  Preencher Admin Ativo
                </button>
                <span className="text-white/20">•</span>
                <button
                  type="button"
                  onClick={() => {
                    setEmail('inativo@decolacont.com.br');
                    setPassword('••••••••••••');
                    setErrorMessage(null);
                  }}
                  className="text-[10px] underline hover:text-white text-brand-offWhite/60"
                >
                  Simular Admin Inativo (Teste)
                </button>
              </div>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isLoading}
            className="w-full font-bold shadow-md"
          >
            Acessar Painel →
          </Button>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-xs text-brand-offWhite/60 hover:text-white underline"
            >
              Voltar ao site público
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
